/**
 * 「看前台」直达链接。
 *
 * 用真正的 `<a>` 而不是 Button（Button 只会渲染 `<button>`）：客户经常想
 * 中键开新标签、右键复制地址，`<button>` + `window.open` 两样都做不到，
 * 而且状态栏不会显示要去哪。样式复用 `.ui-btn` 那套类，看起来仍是同一排按钮。
 *
 * 地址为 null（没绑域名、或商品还没上架）时退化成禁用按钮而不是隐藏：
 * 藏起来的按钮会让人以为"这个商品没有前台页"，禁用 + 说明才说得清原因。
 */
export function FrontLink({
  href,
  label = '看前台',
  ariaLabel,
  unavailableHint,
  hint,
  variant = 'subtle',
}: {
  /** null = 现在打不开，原因写在 unavailableHint 里 */
  href: string | null;
  label?: string;
  /** 每行文字都一样，给读屏器补上"哪一个商品" */
  ariaLabel?: string;
  /** 打不开的原因（禁用时的 title）。不传则退回 hint */
  unavailableHint?: string;
  /** 打得开时的补充说明（例如"显示的是已保存的内容"） */
  hint?: string;
  variant?: 'subtle' | 'ghost';
}) {
  const classes = `ui-btn ui-btn--${variant} ui-btn--sm site-link`;

  if (!href) {
    return (
      <button
        type="button"
        className={`${classes} site-link--off`}
        disabled
        title={unavailableHint ?? hint}
        aria-label={ariaLabel}
      >
        {label}
      </button>
    );
  }

  return (
    <a
      className={classes}
      href={href}
      target="_blank"
      /* noopener：别让客户站点反过来操纵控制台窗口；
         noreferrer：控制台地址不该出现在客户站的访问日志里 */
      rel="noopener noreferrer"
      title={hint ? `${hint}\n${href}` : `在新窗口打开：${href}`}
      aria-label={ariaLabel}
    >
      {label}
      <span className="site-link__mark" aria-hidden="true">
        ↗
      </span>
    </a>
  );
}
